import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/*
 * HOVE cart extras (slide cart + /cart page).
 *
 * - Shipping protection: ON by default. Whenever the cart holds at least one "main" item (anything that is not
 *   protection / warranty) and the shopper has not opted out, exactly one protection line is kept in the cart.
 *   Switching it off removes the line and stores the opt-out in the cart attribute `_hv_sp_optout=1`; switching it
 *   back on clears the attribute and adds the line again.
 * - 2 year warranty: OFF by default, switch adds / removes one line (quantity is always kept at 1).
 * - When no main items remain, protection and warranty are removed.
 * - "Buy extra refills now" cards: [data-hv-cx-add="<variant id>"] adds one of that variant.
 *
 * Every mutation goes through /cart/*.js with `sections` for each <cart-items-component>, and dispatches Horizon's
 * CartLinesUpdateEvent (from '@shopify/events') so the drawer / cart page morph, and the header bubble updates.
 * Events dispatched by this module are ignored by its own listener, and the sync is idempotent, debounced,
 * serialised and rate-limited, so auto-add can never loop.
 *
 * Config + initial state come from window.HoveCartExtrasConfig (snippets/hove-cart-extras.liquid, part 'head').
 */

const SOURCE = 'hove-cart-extras';

/** @returns {any} */
const config = () => /** @type {any} */ (window).HoveCartExtrasConfig || null;

const routes = () => {
  const r = /** @type {any} */ (window).Theme?.routes || {};
  const root = /** @type {any} */ (window).Shopify?.routes?.root || '/';
  const cartUrl = r.cart_url || `${root}cart`;
  return {
    add: r.cart_add_url || `${root}cart/add.js`,
    update: `${r.cart_update_url || `${root}cart/update`}.js`,
    cart: `${cartUrl}.js`,
  };
};

const sectionIds = () => [
  ...new Set(
    [...document.querySelectorAll('cart-items-component')]
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean)
  ),
];

async function getCart() {
  const res = await fetch(routes().cart, { headers: { Accept: 'application/json' }, cache: 'no-store', credentials: 'same-origin' });
  if (!res.ok) throw new Error(`Cart fetch failed: ${res.status}`);
  return res.json();
}

/* ---------------------------------------------------------------- classification */

/**
 * @param {any} item
 * @param {any} def - config.protection | config.warranty
 */
function matches(item, def) {
  if (!def || !def.enabled) return false;
  const ids = (def.variantIds || []).map(String);
  if (def.variantId) ids.push(String(def.variantId));
  return ids.includes(String(item.variant_id)) || (!!def.productId && String(item.product_id) === String(def.productId));
}

/** @param {any} cart */
function classify(cart) {
  const c = config();
  /** @type {any[]} */ const protection = [];
  /** @type {any[]} */ const warranty = [];
  let main = 0;
  for (const item of cart?.items || []) {
    if (matches(item, c?.protection)) protection.push(item);
    else if (matches(item, c?.warranty)) warranty.push(item);
    else main += Number(item.quantity) || 0;
  }
  const qty = (/** @type {any[]} */ lines) => lines.reduce((sum, l) => sum + (Number(l.quantity) || 0), 0);
  return { protection, warranty, main, protectionQty: qty(protection), warrantyQty: qty(warranty) };
}

/**
 * Adds the /cart/update.js `updates` entries needed so that `lines` total exactly `want`.
 * Existing lines are addressed by line key; a missing line is added by variant id.
 * @param {Record<string, number>} updates
 * @param {any[]} lines
 * @param {number} want - 0 or 1
 * @param {string|number} variantId
 * @returns {boolean} whether anything changes
 */
function applyWant(updates, lines, want, variantId) {
  let changed = false;
  if (!lines.length) {
    if (want > 0 && variantId) {
      updates[String(variantId)] = want;
      changed = true;
    }
    return changed;
  }
  lines.forEach((line, i) => {
    const target = i === 0 ? want : 0;
    if (Number(line.quantity) !== target) {
      updates[line.key] = target;
      changed = true;
    }
  });
  return changed;
}

/* ---------------------------------------------------------------- mutations */

let ownDispatch = false;

/**
 * POSTs a cart mutation with section rendering and dispatches Horizon's CartLinesUpdateEvent around it.
 * @param {{ url: string, body: object, action: 'add' | 'update' | 'remove', lines: any[], target?: Element | null }} opts
 */
async function mutate({ url, body, action, lines, target }) {
  const el = target && target.isConnected ? target : document.body;
  const deferred = CartLinesUpdateEvent.createPromise();
  const sections = sectionIds();

  ownDispatch = true;
  try {
    el.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise }));
  } finally {
    ownDispatch = false;
  }

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ ...body, sections: sections.join(','), sections_url: window.location.pathname }),
    });
    const json = await res.json();
    if (!res.ok || json.status || json.errors) {
      throw new Error(json.description || json.message || json.errors || 'Could not update your bag');
    }
    // /cart/update.js returns the cart; /cart/add.js only returns the added items.
    const cart = typeof json.item_count === 'number' && Array.isArray(json.items) ? json : await getCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        sections: json.sections,
        source: SOURCE,
        sourceId: SOURCE,
        didError: false,
      },
    });
    return cart;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    try {
      el.dispatchEvent(new CartErrorEvent({ error: message, code: /** @type {any} */ ('INVALID') }));
    } catch (_) {
      /* no-op */
    }
    // Resolve with the real cart and no section HTML so Horizon re-renders the true state (undoes optimistic UI).
    const cart = await getCart().catch(() => null);
    if (cart) {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: cart.item_count, source: SOURCE, sourceId: SOURCE, didError: true },
      });
    } else {
      deferred.reject(error);
    }
    throw error;
  }
}

/* ---------------------------------------------------------------- sync (auto add / remove) */

let queue = Promise.resolve();
/** @type {ReturnType<typeof setTimeout> | undefined} */
let syncTimer;
/** @type {number[]} */
let syncWrites = [];
/** variant ids whose auto-add failed this page view (e.g. unavailable) – not retried */
const failedAdds = new Set();

/** @param {() => Promise<any>} task */
const enqueue = (task) => {
  const run = queue.then(task, task);
  queue = run.catch(() => {});
  return run;
};

let syncPending = false;
let valveHits = 0;
function scheduleSync(delay = 250) {
  syncPending = true;
  clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    enqueue(runSync).catch((e) => console.warn('[hove-cart-extras] sync failed:', e)).finally(() => { syncPending = false; });
  }, delay);
}
// If the shopper hits Check out while protection is still being added, finish that first, then continue to checkout.
document.addEventListener('click', (e) => {
  const btn = e.target instanceof Element ? e.target.closest('button[name="checkout"], [name="checkout"]') : null;
  if (!btn || !syncPending) return;
  e.preventDefault(); e.stopImmediatePropagation(); clearTimeout(syncTimer);
  enqueue(runSync).catch(() => {}).finally(() => {
    syncPending = false;
    if (btn.form && btn.form.requestSubmit) btn.form.requestSubmit(btn); else btn.click();
  });
}, true);

async function runSync() {
  const c = config();
  if (!c || (!c.protection?.enabled && !c.warranty?.enabled)) return;

  // Safety valve: never write more than 3 automatic corrections in 10 seconds (e.g. an app fighting over the line).
  const now = Date.now();
  syncWrites = syncWrites.filter((t) => now - t < 10000);
  if (syncWrites.length >= 3) { if (++valveHits <= 4) scheduleSync(10050 - (now - syncWrites[0])); return; }

  const cart = await getCart();
  const { protection, warranty, main, warrantyQty } = classify(cart);
  const optOut = String(cart.attributes?.[c.optOutAttribute || '_hv_sp_optout'] || '') === '1';

  /** @type {Record<string, number>} */
  const updates = {};
  let changed = false;

  if (c.protection?.enabled) {
    const want = main > 0 && !optOut ? 1 : 0;
    // Never re-try an automatic add that already failed on this page view; removals / normalising still run.
    if (!(want === 1 && protection.length === 0 && failedAdds.has(String(c.protection.variantId)))) {
      changed = applyWant(updates, protection, want, c.protection.variantId) || changed;
    }
  }
  if (c.warranty?.enabled) {
    const want = main > 0 && warrantyQty > 0 ? 1 : 0; // never auto-added, only normalised / removed
    changed = applyWant(updates, warranty, want, c.warranty.variantId) || changed;
  }
  if (!changed) return;

  syncWrites.push(now);
  const lines = Object.entries(updates).map(([id, quantity]) => ({ id, quantity }));
  try {
    await mutate({
      url: routes().update,
      body: { updates },
      action: 'update',
      lines,
      target: document.querySelector('[data-hv-cx-toggles]'),
    });
  } catch (error) {
    const pv = String(c.protection?.variantId || '');
    if (pv && updates[pv] > 0) {
      failedAdds.add(pv);
      document.querySelectorAll('[data-hv-cx-toggle="protection"]').forEach((s) => s.setAttribute('aria-checked', 'false'));
    }
    throw error;
  }
}

/* ---------------------------------------------------------------- UI: switches + refill cards */

/**
 * @param {Element | null} scope
 * @param {string} message
 */
function showError(scope, message) {
  const box = /** @type {HTMLElement | null} */ (
    scope?.closest('.hv-cx')?.querySelector('[data-hv-cx-error]') || document.querySelector('[data-hv-cx-error]')
  );
  if (!box) return;
  box.textContent = message;
  box.hidden = false;
  setTimeout(() => {
    box.hidden = true;
  }, 5000);
}

/** @param {HTMLElement} sw */
function onToggle(sw) {
  if (sw.getAttribute('aria-busy') === 'true') return;
  const c = config();
  const kind = sw.dataset.hvCxToggle === 'warranty' ? 'warranty' : 'protection';
  const def = c?.[kind];
  if (!def) return;

  const next = sw.getAttribute('aria-checked') !== 'true';
  sw.setAttribute('aria-checked', String(next)); // optimistic
  sw.setAttribute('aria-busy', 'true');
  clearTimeout(syncTimer);

  enqueue(async () => {
    const cart = await getCart();
    const { protection, warranty } = classify(cart);
    /** @type {Record<string, number>} */
    const updates = {};
    applyWant(updates, kind === 'protection' ? protection : warranty, next ? 1 : 0, def.variantId);

    /** @type {Record<string, any>} */
    const body = { updates };
    if (kind === 'protection') {
      body.attributes = { [c.optOutAttribute || '_hv_sp_optout']: next ? '' : '1' };
      if (next) failedAdds.delete(String(def.variantId));
    }
    const lines = Object.entries(updates).map(([id, quantity]) => ({ id, quantity }));
    await mutate({
      url: routes().update,
      body,
      action: next ? 'update' : 'remove',
      lines: lines.length ? lines : [{ id: String(def.variantId), quantity: next ? 1 : 0 }],
      target: sw,
    });
  })
    .catch((error) => {
      sw.setAttribute('aria-checked', String(!next));
      showError(sw, error instanceof Error ? error.message : 'Could not update your bag');
    })
    .finally(() => sw.removeAttribute('aria-busy'));
}

/** @param {HTMLButtonElement} btn */
function onAddRefill(btn) {
  if (btn.disabled) return;
  const id = btn.dataset.hvCxAdd;
  if (!id) return;
  btn.disabled = true;
  btn.setAttribute('aria-busy', 'true');
  clearTimeout(syncTimer);

  enqueue(() =>
    mutate({
      url: routes().add,
      body: { items: [{ id: Number(id), quantity: 1 }] },
      action: 'add',
      lines: [{ merchandiseId: String(id), quantity: 1 }],
      target: btn,
    })
  )
    .then(() => scheduleSync())
    .catch((error) => showError(btn, error instanceof Error ? error.message : 'Could not add to bag'))
    .finally(() => {
      if (btn.isConnected) {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
      }
    });
}

document.addEventListener('click', (event) => {
  const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
  if (!target) return;
  const sw = /** @type {HTMLElement | null} */ (target.closest('[data-hv-cx-toggle]'));
  if (sw) {
    event.preventDefault();
    onToggle(sw);
    return;
  }
  const add = /** @type {HTMLButtonElement | null} */ (target.closest('[data-hv-cx-add]'));
  if (add) {
    event.preventDefault();
    onAddRefill(add);
  }
});

/* ---------------------------------------------------------------- listen to every other cart change */

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (ownDispatch) return; // our own mutations already leave the cart in the desired state
  const promise = /** @type {any} */ (event).promise;
  if (promise && typeof promise.then === 'function') {
    promise.then(
      () => scheduleSync(),
      () => scheduleSync(600)
    );
  } else {
    scheduleSync(600);
  }
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) scheduleSync(0);
});

/* ---------------------------------------------------------------- initial check (only when the rendered state is off) */

(function initialCheck() {
  const c = config();
  const s = c?.state;
  if (!c || !s) return;
  let needed = false;
  if (c.protection?.enabled) {
    const want = s.main > 0 && !s.optOut ? 1 : 0;
    if (s.protection !== want) needed = true;
  }
  if (c.warranty?.enabled && (s.warranty > 1 || (s.main === 0 && s.warranty > 0))) needed = true;
  if (needed) scheduleSync(0);
})();

/** @type {any} */ (window).HoveCartExtras = { sync: () => enqueue(runSync) };
